import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, House as HouseIcon, User as UserIcon, MagnifyingGlass, FilePdf,
         X, CalendarBlank, CheckCircle, Signature, ImageSquare, Wrench } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import PhotoLightbox from "@/components/PhotoLightbox";
import { useIndustry } from "@/context/IndustryContext";

const KIND_COLOR = {
  move_in: "bg-emerald-600",
  move_out: "bg-rose-600",
  turn: "bg-violet-600",
  periodic: "bg-cyan-600",
  // Pest-control kinds
  initial_service: "bg-emerald-600",
  recurring_treatment: "bg-cyan-600",
  callback: "bg-orange-600",
  emergency: "bg-red-600",
  inspection: "bg-sky-600",
  custom: "bg-slate-600",
};

// Per-industry filter chip config. PM (property management) sees the full
// inspection breakdown. Every other vertical (pest, hvac, roofing, etc.)
// only sees All + Custom — the PM-only kinds (move_in/out/turn/periodic)
// don't exist in their workflow and shouldn't clutter the UI.
const INDUSTRY_CHIP_CONFIG = {
  pm: {
    title: "Completed Reports",
    subtitle: "Finished Move-In, Move-Out, Turn, Periodic, and Tenant-guided inspections — polished on-screen with a one-click PDF export.",
    showPeriodicFix: true,
    chips: [
      { key: "move_in", label: "Move-In", color: "emerald" },
      { key: "move_out", label: "Move-Out", color: "rose" },
      { key: "turn", label: "Turns", color: "violet" },
      { key: "periodic", label: "Periodic", color: "cyan" },
      { key: "custom", label: "Custom" },
    ],
  },
  pest_control: {
    title: "Completed Service Reports",
    subtitle: "Finished pest-control service reports with signed sign-off and photo evidence — one-click PDF export.",
    showPeriodicFix: false,
    chips: [
      { key: "initial_service",     label: "Initial",    color: "emerald" },
      { key: "recurring_treatment", label: "Recurring",  color: "cyan" },
      { key: "callback",            label: "Callback",   color: "orange" },
      { key: "emergency",           label: "Emergency",  color: "red" },
      { key: "inspection",          label: "Inspection", color: "sky" },
      { key: "custom",              label: "Other" },
    ],
  },
  // Sensible default for any other vertical (hvac, roofing, etc.)
  _default: {
    title: "Completed Reports",
    subtitle: "Finished service reports with signed sign-off — one-click PDF export.",
    showPeriodicFix: false,
    chips: [
      { key: "custom", label: "Reports" },
    ],
  },
};
const COND_COLOR = {
  excellent: "bg-emerald-600", great: "bg-emerald-600",
  good: "bg-sky-600",
  fair: "bg-amber-500",
  poor: "bg-red-600",
  broken: "bg-red-800", damaged: "bg-red-900",
  na: "bg-slate-400",
  "": "bg-slate-300",
};

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

export default function AdminCompletedInspections() {
  const industryCtx = useIndustry();
  const industry = industryCtx?.industry || "pm";
  const chipCfg = INDUSTRY_CHIP_CONFIG[industry] || INDUSTRY_CHIP_CONFIG._default;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [landlordFilter, setLandlordFilter] = useState("all");
  const [openItem, setOpenItem] = useState(null); // { source, id }
  const [fixing, setFixing] = useState(false);

  const runFixPeriodicTags = async () => {
    if (!window.confirm("Rewrite every 'Move-In' tag on periodic-sweep photos to 'Periodic'? Safe to run any time — only touches photos that were captured on a periodic job.")) return;
    setFixing(true);
    try {
      const r = await api.post("/admin/bootstrap/fix-periodic-photo-tags");
      const d = r.data;
      toast.success(
        d.photos_relabeled > 0
          ? `Relabeled ${d.photos_relabeled} photo${d.photos_relabeled === 1 ? "" : "s"} across ${d.jobs_updated} job${d.jobs_updated === 1 ? "" : "s"}.`
          : `All ${d.periodic_jobs_scanned} periodic report${d.periodic_jobs_scanned === 1 ? "" : "s"} already tagged correctly.`
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fix failed");
    } finally { setFixing(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/completed-inspections");
      setRows(r.data.inspections || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const landlordList = useMemo(() => {
    const set = new Set();
    rows.forEach(r => r.landlord_name && set.add(r.landlord_name));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (kindFilter !== "all") out = out.filter(r => r.kind === kindFilter);
    if (landlordFilter !== "all") out = out.filter(r => r.landlord_name === landlordFilter);
    if (q.trim()) {
      const qL = q.toLowerCase();
      out = out.filter(r =>
        (r.property_address || "").toLowerCase().includes(qL) ||
        (r.tenant_name || "").toLowerCase().includes(qL) ||
        (r.landlord_name || "").toLowerCase().includes(qL) ||
        (r.work_order_number || "").toLowerCase().includes(qL) ||
        (r.signed_by || "").toLowerCase().includes(qL)
      );
    }
    return out;
  }, [rows, q, kindFilter, landlordFilter]);

  const counts = useMemo(() => {
    const c = { all: rows.length, move_in: 0, move_out: 0, turn: 0, periodic: 0, custom: 0 };
    rows.forEach(r => { c[r.kind] = (c[r.kind] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <AdminShell>
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="completed-inspections-page">
        <div className="flex items-center gap-3 flex-wrap">
          <FileText weight="fill" size={30} className="text-violet-600"/>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">{chipCfg.title}</h1>
          {chipCfg.showPeriodicFix && (
            <button onClick={runFixPeriodicTags} disabled={fixing}
              data-testid="fix-periodic-tags-btn"
              title="Rewrite legacy 'Move-In' tags on periodic-sweep photos to 'Periodic'."
              className="ml-auto h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 shadow-sm">
              <Wrench weight="fill" size={13}/> {fixing ? "Fixing…" : "Fix Periodic Tags"}
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          {chipCfg.subtitle}
        </p>

        <div className="mt-5 flex flex-wrap gap-2 items-center" data-testid="ci-filters">
          <FilterChip active={kindFilter === "all"} onClick={() => setKindFilter("all")} testid="ci-kind-all">All ({counts.all})</FilterChip>
          {chipCfg.chips.map(c => (
            <FilterChip key={c.key}
              active={kindFilter === c.key}
              onClick={() => setKindFilter(c.key)}
              testid={`ci-kind-${c.key}`}
              color={c.color}>
              {c.label} ({counts[c.key] || 0})
            </FilterChip>
          ))}
          {landlordList.length > 0 && (
            <select value={landlordFilter} onChange={e => setLandlordFilter(e.target.value)} data-testid="ci-landlord-filter"
              className="h-9 px-3 border-2 border-[#0f172a] text-xs font-bold">
              <option value="all">All Landlords</option>
              {landlordList.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          <div className="ml-auto relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input list="known-addresses" autoComplete="off" placeholder="Search address, tenant, WO#…" value={q} onChange={e => setQ(e.target.value)}
              data-testid="ci-search"
              className="w-full h-9 pl-7 pr-3 border-2 border-[#0f172a] text-xs font-medium"/>
          </div>
        </div>

        {loading && <div className="mt-8 text-center text-slate-500 font-bold uppercase text-xs">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="mt-6 rugged-card p-10 text-center" data-testid="ci-empty">
            <FileText size={48} className="mx-auto text-slate-300"/>
            <div className="mt-3 font-display font-black text-lg uppercase text-slate-500">
              {industry === "pest_control" ? "No completed service reports yet" : "No completed reports yet"}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {industry === "pm"
                ? "Finished Turn / Move-In / Move-Out / TenantRadar inspections will show up here."
                : "Finished reports with a signed sign-off will show up here."}
            </div>
          </div>
        )}
        {!loading && rows.length > 0 && filtered.length === 0 && (
          <div className="mt-6 text-center text-slate-500 font-bold uppercase text-xs" data-testid="ci-no-match">No reports match your filters.</div>
        )}

        {filtered.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="ci-rows">
            {filtered.map(row => {
              const key = `${row.source}:${row.id}`;
              return (
                <button key={key} data-testid={`ci-row-${row.source}-${row.id}`}
                  onClick={() => setOpenItem({ source: row.source, id: row.id })}
                  className="w-full text-left rugged-card p-4 hover:bg-slate-50 transition flex flex-wrap gap-3 items-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${KIND_COLOR[row.kind] || "bg-slate-600"}`}>
                    {row.kind_label}
                  </span>
                  <div className="flex-1 min-w-[260px]">
                    <div className="font-black text-sm">
                      {row.property_address}{row.unit ? ` · Unit ${row.unit}` : ""}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      {row.tenant_name && <span className="inline-flex items-center gap-1"><UserIcon size={11}/> {row.tenant_name}</span>}
                      {row.landlord_name && <span className="inline-flex items-center gap-1"><HouseIcon size={11}/> {row.landlord_name}</span>}
                      {row.work_order_number && <span className="font-mono text-slate-500">WO# {row.work_order_number}</span>}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="inline-flex items-center gap-1 font-black uppercase tracking-widest text-emerald-700">
                      <CheckCircle weight="fill" size={12}/> Completed
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(row.completed_at)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {openItem && <ReportDrawer source={openItem.source} id={openItem.id} onClose={() => setOpenItem(null)}/>}
    </AdminShell>
  );
}

function FilterChip({ active, onClick, children, testid, color }) {
  const activeCls = color === "emerald" ? "bg-emerald-600 border-emerald-600 text-white"
    : color === "rose" ? "bg-rose-600 border-rose-600 text-white"
    : color === "violet" ? "bg-violet-600 border-violet-600 text-white"
    : color === "cyan" ? "bg-cyan-600 border-cyan-600 text-white"
    : color === "orange" ? "bg-orange-600 border-orange-600 text-white"
    : color === "red" ? "bg-red-600 border-red-600 text-white"
    : color === "sky" ? "bg-sky-600 border-sky-600 text-white"
    : "bg-[#0f172a] border-[#0f172a] text-white";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-9 px-3 font-black uppercase tracking-widest text-[11px] border-2 ${
        active ? activeCls : "border-slate-300 text-slate-700 hover:border-[#0f172a] bg-white"
      }`}>
      {children}
    </button>
  );
}

function ReportDrawer({ source, id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/completed-inspections/${source}/${id}`)
      .then(r => setData(r.data))
      .catch(e => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load report"));
  }, [source, id]);

  const token = typeof window !== "undefined" ? (localStorage.getItem("access_token") || localStorage.getItem("token") || "") : "";
  const pdfUrl = data?.pdf_url
    ? `${api.defaults.baseURL}${data.pdf_url.replace(/^\/api/, "")}?auth=${encodeURIComponent(token)}`
    : null;

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose} data-testid="ci-drawer">
      <div className="bg-white w-full max-w-4xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0f172a] text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-black text-lg uppercase">Finished Report</div>
          <button onClick={onClose} className="p-1 hover:bg-white/20" data-testid="ci-drawer-close"><X size={22}/></button>
        </div>
        {err && <div className="p-6 text-red-600 font-bold" data-testid="ci-drawer-error">{err}</div>}
        {!err && !data && <div className="p-6 text-slate-500 font-bold uppercase tracking-widest">Loading…</div>}
        {data && (
          <FinishedReport data={data} pdfUrl={pdfUrl}/>
        )}
      </div>
    </div>
  );
}

export function FinishedReport({ data, pdfUrl }) {
  const [lightbox, setLightbox] = useState(null); // { photos, index, label }
  const [retagBusy, setRetagBusy] = useState(false);
  const [retagRoom, setRetagRoom] = useState({}); // per-phase choice: { move_out: "Kitchen" }
  const [refreshTick, setRefreshTick] = useState(0);
  const token = typeof window !== "undefined" ? (localStorage.getItem("access_token") || localStorage.getItem("token") || "") : "";
  const withAuth = (url) => `${url}${url.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}`;

  const extractPath = (url) => {
    // Extract the storage path from /api/files/<path>?auth=…
    try {
      const m = url.match(/\/api\/files\/([^?]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch { return null; }
  };

  const tenantRooms = (() => {
    const s = new Set();
    (data.phases || []).forEach(p => (p.rooms || []).forEach(r => {
      if (r.name && !r.name.startsWith("Additional Photos")) s.add(r.name);
    }));
    return Array.from(s);
  })();

  const applyRetag = async (phaseKey, photoUrls) => {
    const room = retagRoom[phaseKey];
    if (!room) { toast?.error?.("Pick a room first"); return; }
    const paths = photoUrls.map(extractPath).filter(Boolean);
    if (!paths.length) { toast?.error?.("Could not read photo paths"); return; }
    if (!data.source_job_id && data.source !== "job") { toast?.error?.("Retag only supported for job-based inspections"); return; }
    setRetagBusy(true);
    try {
      await api.post(`/jobs/${data.id}/photos/retag-bulk`, { paths, room, phase: phaseKey });
      toast.success(`Tagged ${paths.length} photo${paths.length===1?"":"s"} → ${room}`);
      setRefreshTick(t => t + 1);
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Retag failed");
    } finally {
      setRetagBusy(false);
    }
  };

  return (
    <div className="p-5 space-y-6" data-testid="ci-finished-report" data-refresh={refreshTick}>
      {/* Header */}
      <div className="border-b-4 border-[#0f172a] pb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${KIND_COLOR[data.kind] || "bg-slate-600"}`}>
            {data.kind_label}
          </span>
          <span className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-emerald-600 text-white inline-flex items-center gap-1">
            <CheckCircle weight="fill" size={12}/> Completed
          </span>
          {data.work_order_number && <span className="text-xs font-mono text-slate-500">WO# {data.work_order_number}</span>}
        </div>
        <h2 className="font-display font-black text-2xl sm:text-3xl mt-3 leading-tight">
          {data.property_address}{data.unit ? ` · Unit ${data.unit}` : ""}
        </h2>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
          {data.tenant_name && <div><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Tenant:</span> <b>{data.tenant_name}</b></div>}
          {data.landlord_name && <div><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Landlord:</span> <b>{data.landlord_name}</b></div>}
          {data.assigned_to_name && <div><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Inspector:</span> <b>{data.assigned_to_name}</b></div>}
          {data.signed_by && <div className="inline-flex items-center gap-1"><Signature size={13}/><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Signed:</span> <b>{data.signed_by}</b></div>}
          {data.completed_at && <div className="inline-flex items-center gap-1"><CalendarBlank size={13}/><span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Completed:</span> <b>{fmtDate(data.completed_at)}</b></div>}
        </div>
      </div>

      {/* PDF Download + Evidence Zip */}
      <div className="flex flex-col sm:flex-row gap-2">
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="ci-download-pdf"
            className="flex-1 h-12 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2 hover:opacity-90">
            <FilePdf size={16}/> Download Full PDF Report
          </a>
        )}
        {data.source === "job" && data.id && (
          <a href={`${api.defaults.baseURL}/jobs/${data.id}/evidence-bundle?auth=${encodeURIComponent(token)}`}
            target="_blank" rel="noreferrer"
            data-testid="ci-evidence-bundle"
            title="Untouched original photos + manifest — for security-hold defense"
            className="flex-shrink-0 h-12 px-4 bg-slate-800 hover:bg-slate-900 text-white border-2 border-[#0f172a] font-black uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2">
            🔒 Evidence Zip
          </a>
        )}
      </div>

      {/* Phases */}
      {(data.phases || []).map((phase, pi) => (
        <section key={pi} data-testid={`ci-phase-${phase.phase_key}`}>
          <div className="flex items-center gap-2 mb-3">
            <div className={`h-1 flex-1 ${phase.phase_key === "move_out" ? "bg-rose-600" : phase.phase_key === "move_in" ? "bg-emerald-600" : "bg-cyan-600"}`}/>
            <h3 className="font-display font-black uppercase tracking-widest text-sm">{phase.phase_label}</h3>
            <div className={`h-1 flex-1 ${phase.phase_key === "move_out" ? "bg-rose-600" : phase.phase_key === "move_in" ? "bg-emerald-600" : "bg-cyan-600"}`}/>
          </div>
          {(phase.rooms || []).length === 0 && (
            <div className="text-xs text-slate-500 italic">No room data recorded.</div>
          )}
          <div className="space-y-3">
            {(phase.rooms || []).map((room, ri) => {
              const isAdditional = (room.name || "").startsWith("Additional Photos");
              const allRoomPhotos = (room.items || []).flatMap(it => it.photos || []);
              return (
              <div key={ri} className="border-2 border-slate-200 p-3" data-testid={`ci-room-${phase.phase_key}-${ri}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="font-display font-black text-base uppercase">{room.name}</div>
                  {isAdditional && data.source === "job" && tenantRooms.length > 0 && allRoomPhotos.length > 0 && (
                    <div className="flex items-center gap-2" data-testid={`ci-retag-controls-${phase.phase_key}`}>
                      <select value={retagRoom[phase.phase_key] || ""}
                        onChange={(e) => setRetagRoom(prev => ({ ...prev, [phase.phase_key]: e.target.value }))}
                        data-testid={`ci-retag-select-${phase.phase_key}`}
                        className="h-8 px-2 border-2 border-[#0f172a] text-[11px] font-bold">
                        <option value="">Tag all → room…</option>
                        {tenantRooms.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <button onClick={() => applyRetag(phase.phase_key, allRoomPhotos)}
                        disabled={retagBusy || !retagRoom[phase.phase_key]}
                        data-testid={`ci-retag-apply-${phase.phase_key}`}
                        className="h-8 px-3 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40">
                        Tag {allRoomPhotos.length}
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-2 space-y-3">
                  {(room.items || []).map((item, ii) => {
                    const cond = (item.condition || "").toLowerCase();
                    const condColor = COND_COLOR[cond] || "bg-slate-300";
                    const hasContent = cond || item.notes || (item.photos && item.photos.length > 0);
                    return (
                      <div key={ii} className="border-t pt-2 first:border-t-0 first:pt-0"
                        data-testid={`ci-item-${phase.phase_key}-${ri}-${ii}`}>
                        <div className="flex items-start gap-2 flex-wrap">
                          <div className="flex-1 min-w-[180px] text-sm font-bold">{item.label}</div>
                          <span className={`inline-flex items-center px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white ${condColor}`}>
                            {cond ? cond : "Not Answered"}
                          </span>
                        </div>
                        {item.notes && <div className="mt-1 text-xs italic text-slate-700 whitespace-pre-wrap">{item.notes}</div>}
                        {(item.photos || []).length > 0 && (
                          <div className="mt-2">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                              📸 {item.photos.length} photo{item.photos.length !== 1 ? "s" : ""} · tap any to open the gallery
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {item.photos.map((url, pIdx) => {
                                const authed = withAuth(url);
                                return (
                                  <button key={pIdx} type="button"
                                    onClick={() => {
                                      const rooms = (data.phases || [])
                                        .flatMap(ph => (ph.rooms || []).map(r => r.name))
                                        .filter((r, i, a) => r && a.indexOf(r) === i);
                                      setLightbox({
                                        photos: item.photos.map(withAuth),
                                        quickTagPaths: item.photos.map(extractPath),
                                        quickTagRooms: rooms,
                                        quickTagCurrent: room.name,
                                        index: pIdx,
                                        label: `${room.name} · ${phase.phase_label}`,
                                        onQuickTag: async (path, targetRoom) => {
                                          try {
                                            await api.post(`/jobs/${data.id}/photos/retag-bulk`, {
                                              paths: [path], room: targetRoom, phase: phase.phase_key,
                                            });
                                            toast.success(`Tagged → ${targetRoom}`);
                                            setRefreshTick(t => t + 1);
                                            return true;
                                          } catch (e) {
                                            toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Tag failed"));
                                            return false;
                                          }
                                        },
                                      });
                                    }}
                                    data-testid={`ci-photo-${phase.phase_key}-${ri}-${ii}-${pIdx}`}
                                    className="focus:outline-none focus:ring-2 focus:ring-[#00C4CC] relative group">
                                    <img src={authed} alt="" className="h-24 w-24 object-cover border-2 border-[#0f172a] flex-shrink-0 hover:opacity-80 transition"/>
                                    <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[9px] font-black px-1">{pIdx + 1}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {!hasContent && <div className="mt-1 text-[11px] text-slate-400 italic inline-flex items-center gap-1"><ImageSquare size={11}/> No data captured.</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </section>
      ))}
      {lightbox && (
        <PhotoLightbox
          photos={lightbox.photos}
          startIndex={lightbox.index}
          label={lightbox.label}
          quickTagRooms={lightbox.quickTagRooms || []}
          quickTagPaths={lightbox.quickTagPaths || null}
          quickTagCurrent={lightbox.quickTagCurrent || null}
          onQuickTag={lightbox.onQuickTag || null}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
